/**
 * ChartOverview — the Overview tab of the patient chart (prototype `PatientChart` Overview, 19 Oct): side by side, the one
 * white card each for Documents (count and providers, provider chips with counts, the most recent five with an icon per
 * type and the date), Medications (current and stopped, the current list) and Problems and allergies (count, the first
 * items with their severity). Each card's link opens the chart tab that holds the full list.
 *
 * Every number and name is a field of a row an existing hook returns (no new endpoint, nothing invented):
 *   Documents              `usePatientDocuments` (first page)
 *   Medications            `usePatientMedications` (current, and the past list for "stopped")
 *   Problems and allergies `usePatientConditions('Active')`, `usePatientAllergies`
 * The hooks share their query keys with the chart's own tabs, so a card and its tab read one cache entry.
 * Look: the bridge's "patient chart" block, keyed on `data-slot="chart-overview*"`.
 */
import type { ReactNode } from 'react';
import { Icon, Skeleton } from '@scripsteam/scrips-signal-ds';
import { usePatientAllergies } from '@/patients/services/use-patient-allergies';
import { usePatientConditions } from '@/patients/services/use-patient-conditions';
import { usePatientDocuments } from '@/patients/services/use-patient-documents';
import { usePatientMedications } from '@/patients/services/use-patient-medications';
import { liveRows } from '@/patients/lib/overview-headlines';
import {
  countText,
  mostRecentDocuments,
  overviewDate,
  providerCounts,
} from '@/patients/lib/chart-overview';

export const OVERVIEW_DOCUMENTS_PAGE_SIZE = 20;
/** Rows a card lists. */
const LISTED = 5;

export type ChartOverviewTarget = 'documents' | 'medications' | 'problems';

export interface ChartOverviewProps {
  patientId: string;
  /** A card's link: the chart tab that holds the full list. */
  onOpen: (target: ChartOverviewTarget) => void;
}


export function ChartOverview({ patientId, onOpen }: ChartOverviewProps) {
  return (
    <div data-slot="chart-overview">
      <DocumentsCard patientId={patientId} onOpen={() => onOpen('documents')} />
      <MedicationsCard patientId={patientId} onOpen={() => onOpen('medications')} />
      <ProblemsCard patientId={patientId} onOpen={() => onOpen('problems')} />
    </div>
  );
}

function OverviewCard({
  title,
  meta,
  linkLabel,
  onOpen,
  children,
}: {
  title: string;
  meta?: string;
  linkLabel: string;
  onOpen: () => void;
  children: ReactNode;
}) {
  return (
    <section data-slot="chart-overview-card" aria-label={title}>
      <header data-slot="chart-overview-head">
        <h3 data-slot="chart-overview-title">{title}</h3>
        {meta ? <span data-slot="chart-overview-meta">{meta}</span> : null}
        <button type="button" data-slot="chart-overview-link" onClick={onOpen}>
          {linkLabel}
        </button>
      </header>
      {children}
    </section>
  );
}

function State({ loading, error, children }: { loading: boolean; error: boolean; children: ReactNode }) {
  if (loading) return <Skeleton className="h-10 w-full" />;
  if (error) return <p data-slot="chart-overview-empty">Could not load. Try again later.</p>;
  return <>{children}</>;
}

function DocumentsCard({ patientId, onOpen }: { patientId: string; onOpen: () => void }) {
  const q = usePatientDocuments({ patientId, pageSize: OVERVIEW_DOCUMENTS_PAGE_SIZE });
  const docs = liveRows(q.data);
  const providers = providerCounts(docs);
  const recent = mostRecentDocuments(docs, LISTED);
  const total = countText(docs.length, OVERVIEW_DOCUMENTS_PAGE_SIZE);
  const meta =
    docs.length === 0
      ? undefined
      : docs.length >= OVERVIEW_DOCUMENTS_PAGE_SIZE || providers.length === 0
        ? `${total} documents`
        : // The design's words (PaChartOverview Documents meta): "6 documents from 2 providers".
          `${total} ${docs.length === 1 ? 'document' : 'documents'} from ${providers.length} ${providers.length === 1 ? 'provider' : 'providers'}`;
  return (
    <OverviewCard title="Documents" meta={meta} linkLabel={docs.length > 0 ? `Open all ${total}` : 'Open'} onOpen={onOpen}>
      <State loading={q.isLoading} error={q.isError}>
        {docs.length === 0 ? (
          <p data-slot="chart-overview-empty">No documents on the record.</p>
        ) : (
          <>
            {providers.length > 0 ? (
              <ul data-slot="chart-overview-chips" aria-label="Providers">
                {providers.map((p) => (
                  <li key={p.name} data-slot="chart-overview-chip">
                    <Icon name="building" size={16} color="currentColor" aria-hidden />
                    <span data-slot="chart-overview-chip-name">{p.name}</span>
                    <span data-slot="chart-overview-chip-count">{p.count}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            <h4 data-slot="chart-overview-sub">Most recent</h4>
            <ul data-slot="chart-overview-rows">
              {recent.map((d, i) => {
                const date = overviewDate(d.documentDate ?? d.createdOn);
                return (
                  <li key={d.id ?? i} data-slot="chart-overview-row">
                    {/* One document glyph per row, as the design (PaChartOverview `rowIcon` default `file`). */}
                    <Icon name="file" size={16} color="currentColor" aria-hidden />
                    <span data-slot="chart-overview-row-name">{d.name?.trim() || d.type?.trim() || 'Document'}</span>
                    {date ? <span data-slot="chart-overview-row-meta">{date}</span> : null}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </State>
    </OverviewCard>
  );
}

function MedicationsCard({ patientId, onOpen }: { patientId: string; onOpen: () => void }) {
  const currentQ = usePatientMedications(patientId, true);
  const stoppedQ = usePatientMedications(patientId, false);
  const current = liveRows(currentQ.data);
  const stopped = liveRows(stoppedQ.data);
  const loading = currentQ.isLoading || stoppedQ.isLoading;
  const listed = current.slice(0, LISTED);
  return (
    <OverviewCard
      title="Medications"
      meta={loading ? undefined : `${current.length} current · ${stopped.length} stopped`}
      linkLabel="Open"
      onOpen={onOpen}
    >
      <State loading={loading} error={currentQ.isError || stoppedQ.isError}>
        {current.length === 0 ? (
          <p data-slot="chart-overview-empty">No current medications on the record.</p>
        ) : (
          <>
            <ul data-slot="chart-overview-rows">
              {listed.map((m, i) => (
                <li key={m.identifier ?? i} data-slot="chart-overview-row">
                  <Icon name="pill" size={16} color="currentColor" aria-hidden />
                  <span data-slot="chart-overview-row-name">{m.code?.display?.trim() || 'Medication'}</span>
                  {m.frequency?.trim() ? <span data-slot="chart-overview-row-meta">{m.frequency.trim()}</span> : null}
                </li>
              ))}
            </ul>
            {current.length > listed.length ? (
              <p data-slot="chart-overview-more">{current.length - listed.length} more current</p>
            ) : null}
          </>
        )}
      </State>
    </OverviewCard>
  );
}

/** A severity worth showing: not blank, not the wire's "n/a". */
function severityText(s: string | null | undefined): string | null {
  const t = s?.trim();
  return t && t.toLowerCase() !== 'n/a' ? t : null;
}

function ProblemsCard({ patientId, onOpen }: { patientId: string; onOpen: () => void }) {
  const problemsQ = usePatientConditions(patientId, 'Active');
  const allergiesQ = usePatientAllergies(patientId);
  const problems = liveRows(problemsQ.data);
  const allergies = liveRows(allergiesQ.data);
  const loading = problemsQ.isLoading || allergiesQ.isLoading;
  const items = [
    ...problems.map((p, i) => ({
      key: `p${p.identifier ?? i}`,
      name: p.code?.display?.trim() || 'Problem',
      meta: severityText(p.severity),
    })),
    ...allergies.map((a, i) => ({
      key: `a${a.identifier ?? i}`,
      name: a.code?.display?.trim() || 'Allergy',
      meta: severityText(a.criticality?.display),
    })),
  ];
  const listed = items.slice(0, LISTED);
  return (
    <OverviewCard
      title="Problems and allergies"
      meta={loading ? undefined : `${problems.length} ${problems.length === 1 ? 'problem' : 'problems'} · ${allergies.length} ${allergies.length === 1 ? 'allergy' : 'allergies'}`}
      linkLabel="Open"
      onOpen={onOpen}
    >
      <State loading={loading} error={problemsQ.isError || allergiesQ.isError}>
        {items.length === 0 ? (
          <p data-slot="chart-overview-empty">No active problems or allergies on the record.</p>
        ) : (
          <>
            <ul data-slot="chart-overview-rows">
              {listed.map((it) => (
                <li key={it.key} data-slot="chart-overview-row">
                  <Icon name="heart" size={16} color="currentColor" aria-hidden />
                  <span data-slot="chart-overview-row-name">{it.name}</span>
                  {it.meta ? <span data-slot="chart-overview-row-meta">{it.meta}</span> : null}
                </li>
              ))}
            </ul>
            {items.length > listed.length ? (
              <p data-slot="chart-overview-more">{items.length - listed.length} more</p>
            ) : null}
          </>
        )}
      </State>
    </OverviewCard>
  );
}
